// src/components/roles/RolesList.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  alpha,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  PersonAdd as AssignIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Custom components
import { Button } from '../../design-system/components/core';
import { EmptyState } from '../../design-system/components/core';

// Styled components
const RootPaper = styled(Paper)(({ theme }) => ({
  // borderRadius: theme.shape.borderRadius * 2,
  overflow: 'hidden',
  boxShadow: theme.shadows[1],
  backgroundImage: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 0.7)} 0%, ${theme.palette.background.paper} 100%)`,
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  transition: 'transform 0.2s, background-color 0.2s',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}));

const RoleRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s',
  position: 'relative',
  overflow: 'hidden',
  
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 2,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
      backgroundSize: '200% 100%',
      animation: 'shine 2s linear infinite',
    },
  },
  
  '@keyframes shine': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-200% 0' },
  },
}));

const PermissionCount = styled(Chip)(({ theme, count }) => ({
  backgroundColor: count > 0 
    ? alpha(theme.palette.success.main, 0.1) 
    : alpha(theme.palette.error.main, 0.1),
  color: count > 0 ? theme.palette.success.main : theme.palette.error.main,
  fontWeight: 600,
}));

/**
 * Component to display a list of roles with actions
 * 
 * @param {Object} props
 * @param {Array} props.roles - List of roles to display
 * @param {boolean} props.loading - Whether data is loading
 * @param {Object} props.pagination - Pagination info
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {Function} props.onLimitChange - Callback when limit changes
 * @param {Function} props.onDelete - Callback when delete is clicked
 * @param {Function} props.onRefresh - Callback to refresh data
 * @param {Function} props.onAssign - Callback when assign is clicked
 */
const RolesList = ({
  roles = [],
  loading = false,
  pagination = { page: 1, totalPages: 1, totalDocs: 0, limit: 10 },
  onPageChange,
  onLimitChange,
  onDelete,
  onRefresh,
  onAssign,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  
  // Filter roles based on search term
  const filteredRoles = roles.filter(role => 
    role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Navigate to role creation
  const handleCreateRole = () => {
    navigate('/admin/roles/new');
  };
  
  // Navigate to role edit
  const handleEditRole = (roleId) => {
    navigate(`/admin/roles/${roleId}`);
  };
  
  // Open delete confirmation dialog
  const handleDeleteClick = (role) => {
    setRoleToDelete(role);
    setDeleteDialogOpen(true);
  };
  
  // Confirm role deletion
  const handleDeleteConfirm = () => {
    if (roleToDelete) {
      onDelete(roleToDelete.id);
    }
    setDeleteDialogOpen(false);
    setRoleToDelete(null);
  };
  
  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    onPageChange(newPage + 1); // MUI uses 0-indexed pages
  };
  
  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    onLimitChange(parseInt(event.target.value, 10));
  };
  
  // Handle assign role
  const handleAssignRole = (roleId) => {
    onAssign(roleId);
  };
  
  // Loading skeleton
  if (loading && roles.length === 0) {
    return (
      <RootPaper>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Skeleton variant="text" width={200} height={40} />
            <Skeleton variant="rectangular" width={150} height={40} />
          </Box>
          
          <Skeleton variant="rectangular" height={48} sx={{ mb: 2 }} />
          
          {Array(5).fill(0).map((_, index) => (
            <Skeleton key={index} variant="rectangular" height={53} sx={{ mb: 1 }} />
          ))}
          
          <Skeleton variant="rectangular" height={52} sx={{ mt: 2 }} />
        </Box>
      </RootPaper>
    );
  }
  
  return (
    <>
      <RootPaper>
        <Box sx={{ p: 3 }}>
          {/* Header with title and actions */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3
          }}>
            <Typography 
              variant="h5" 
              sx={{ 
                display: 'flex', 
                alignItems: 'center' 
              }}
            >
              <SecurityIcon 
                sx={{ 
                  mr: 1.5, 
                  color: 'primary.main' 
                }} 
              />
              Roles Management
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh">
                <IconButton onClick={onRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              
              <Button
                variant="contained"
                
                startIcon={<AddIcon />}
                onClick={handleCreateRole}
                disabled={loading}
                magical
              >
                Create Role
              </Button>
            </Box>
          </Box>
          
          {/* Search box */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          
          {/* Empty state */}
          {!loading && filteredRoles.length === 0 && (
            <EmptyState
              icon={<SecurityIcon sx={{ fontSize: 64 }} />}
              title="No roles found"
              description={
                searchTerm
                  ? "No roles match your search criteria."
                  : "You haven't created any roles yet."
              }
              actionButton={
                searchTerm ? (
                  <Button 
                    variant="outlined" 
                    onClick={() => setSearchTerm('')}
                  >
                    Clear Search
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateRole}
                    magical
                  >
                    Create First Role
                  </Button>
                )
              }
            />
          )}
          
          {/* Roles table */}
          {filteredRoles.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Key</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="center">Permissions</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRoles.map((role) => (
                    <RoleRow key={role.id}>
                      <TableCell>
                        <Typography variant="subtitle2">{role.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={role.key}
                          size="small"
                          variant="outlined"
                          
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            maxWidth: 300,
                          }}
                        >
                          {role.description}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={
                          role.permissions && role.permissions.length > 0 ? 
                            <div>
                              <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Permissions:</Typography>
                              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                {role.permissions.slice(0, 5).map((perm, idx) => (
                                  <Box component="li" key={idx}>
                                    <Typography variant="caption">
                                      {`${perm.resource}:${perm.action} (${perm.scope})`}
                                    </Typography>
                                  </Box>
                                ))}
                                {role.permissions.length > 5 && (
                                  <Box component="li">
                                    <Typography variant="caption">
                                      {`...and ${role.permissions.length - 5} more`}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </div>
                          : 'No permissions assigned'
                        }>
                          <PermissionCount
                            size="small"
                            count={role.permissions?.length || 0}
                            label={`${role.permissions?.length || 0} permissions`}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                          <Tooltip title="Assign to user">
                            <ActionButton
                              size="small"
                              
                              onClick={() => handleAssignRole(role.id)}
                              disabled={loading}
                            >
                              <AssignIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <ActionButton
                              size="small"
                              
                              onClick={() => handleEditRole(role.id)}
                              disabled={loading}
                            >
                              <EditIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <ActionButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(role)}
                              disabled={loading}
                            >
                              <DeleteIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </RoleRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          
          {/* Pagination */}
          {filteredRoles.length > 0 && (
            <TablePagination
              component="div"
              count={pagination.totalDocs}
              page={pagination.page - 1} // MUI uses 0-indexed pages
              rowsPerPage={pagination.limit}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          )}
        </Box>
      </RootPaper>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the role <strong>{roleToDelete?.name}</strong>? 
            This action cannot be undone, and it may affect users who have this role assigned.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            color="error" 
            onClick={handleDeleteConfirm}
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RolesList;